import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ResetApiErrorMessage } from '@app/features/app-content/states/app-configuration.actions';
import { FetchPublishingDataRequest } from '@app/features/app-publishing/states/publishing.actions';
import { ResetPagination } from '@app/features/notifications/states/notifications.actions';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { AdminService } from '@app/features/admin/services/admin.service';
import { catchError, EMPTY, Observable, tap } from 'rxjs';
import { AuthState } from '../auth/states/auth.state';
import { App } from '../models/app.model';
import { AppUpdateService } from '../services/app-update.service';
import { AppsListService } from '../services/apps-list.service';
import { SwloggerService } from '../services/swlogger.service';
import {
  FetchAppsEmptyError,
  FetchAppsError,
  FetchAppsRequest,
  FetchAppsSuccess,
  ResetAppState,
  ResetCurrentApp,
  SetCurrentApp,
  UnlockUpdateError,
  UnlockUpdateRequest,
  UnlockUpdateSuccess,
  UpdateAvailable,
} from './app.actions';

export class AppStateModel {
  apps: App[];
  isLoading: boolean;
  unlockUpdateIsLoading: boolean;
  currentAppId: number;
  currentApp: App;
  appsManagerHasUpdateAvailable: boolean;
  showReturnToDashboardLink: boolean;
}

@State<AppStateModel>({
  name: 'app',
  defaults: {
    apps: null,
    isLoading: true,
    unlockUpdateIsLoading: false,
    currentAppId: null,
    currentApp: null,
    appsManagerHasUpdateAvailable: false,
    showReturnToDashboardLink: false,
  },
})
@Injectable()
export class AppState {
  constructor(
    private readonly appsListService: AppsListService,
    private readonly adminService: AdminService,
    private readonly appsUpdateService: AppUpdateService,
    private readonly swLogger: SwloggerService,
    private readonly store: Store,
    private readonly router: Router,
  ) {
    this.swLogger.watchForUpdate();
  }

  @Selector()
  static isLoading(state: AppStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static unlockUpdateIsLoading(state: AppStateModel): boolean {
    return state.unlockUpdateIsLoading;
  }

  @Selector()
  static apps(state: AppStateModel): App[] {
    return state.apps;
  }

  @Selector()
  static currentApp(state: AppStateModel): App {
    return state.currentApp;
  }

  @Selector()
  static currentAppId(state: AppStateModel): number {
    return state.currentAppId;
  }

  @Selector()
  static currentAppServiceId(state: AppStateModel): number {
    return AppState.currentApp(state)?.serviceId;
  }

  @Selector()
  static appsManagerHasUpdateAvailable(state: AppStateModel): boolean {
    return state.appsManagerHasUpdateAvailable;
  }

  @Selector()
  static currentAppHasUpdateAvailable(state: AppStateModel): boolean {
    const currentApp = AppState.currentApp(state);

    return currentApp?.androidUpdateAvailable || currentApp?.iosUpdateAvailable;
  }

  @Selector()
  static showReturnToDashboardLink(state: AppStateModel): boolean {
    return state.showReturnToDashboardLink;
  }

  @Selector()
  static isIphoneReady(state: AppStateModel): boolean {
    return AppState.currentApp(state)?.iPhoneReady;
  }

  @Selector()
  static isAndroidReady(state: AppStateModel): boolean {
    return AppState.currentApp(state)?.androidReady;
  }

  @Selector()
  static hasNotificationsEnabled(state: AppStateModel): boolean {
    return AppState.currentApp(state)?.androidNotificationIsAvailable;
  }

  @Selector()
  static currentAppIsNotOwnedByUser(state: AppStateModel): boolean {
    if (!state.currentAppId || !state.apps) {
      return false;
    }

    return !state.apps?.some(app => app.appId === Number(state.currentAppId));
  }

  @Selector()
  static deeplinkIsAvailable(state: AppStateModel): boolean {
    return AppState.currentApp(state)?.deeplinkAvailable;
  }

  @Action(FetchAppsRequest)
  fetchAppsRequest(ctx: StateContext<AppStateModel>) {
    ctx.patchState({
      isLoading: true,
    });

    return this.appsListService.getApps().pipe(
      tap(apps => {
        ctx.dispatch(new FetchAppsSuccess(apps));
      }),
      catchError(err => {
        if (err.error.code === 404) {
          return ctx.dispatch(new FetchAppsEmptyError());
        }

        return ctx.dispatch(new FetchAppsError());
      }),
    );
  }

  @Action(FetchAppsSuccess)
  FetchAppsSuccess(ctx: StateContext<AppStateModel>, { apps }: FetchAppsSuccess) {
    ctx.patchState({
      apps,
    });

    // if no app is selected, defaults to first app in the user app list
    if (!ctx.getState().currentAppId) {
      this.store.dispatch(new SetCurrentApp(apps[0].appId));
    }

    return this.resolveCurrentApp(ctx);
  }

  @Action([FetchAppsSuccess, FetchAppsError])
  toggleLoadingState(ctx: StateContext<AppStateModel>) {
    ctx.patchState({
      isLoading: false,
    });
  }

  @Action(FetchAppsEmptyError)
  fetchAppsEmptyError(ctx: StateContext<AppStateModel>) {
    ctx.patchState({
      isLoading: false,
    });
    const userRKDomain = this.store.selectSnapshot(AuthState.userRKDomain);
    const isAdminOrSuperuser = this.store.selectSnapshot(AuthState.isAdminOrSuperuser);
    if (!isAdminOrSuperuser) {
      window.location.href = `${userRKDomain}/on/myapps.php`;

      return EMPTY;
    }

    // admins without apps can still manage other apps: use an empty list
    ctx.patchState({ apps: [] });

    // without a selected app, send the admin to the dashboard to pick one
    if (!ctx.getState().currentAppId) {
      this.router.navigate(['/admin']);

      return EMPTY;
    }

    return this.resolveCurrentApp(ctx);
  }

  @Action(FetchAppsError)
  fetchAppsError(ctx: StateContext<AppStateModel>) {
    ctx.patchState({
      isLoading: false,
    });
  }

  @Action(SetCurrentApp)
  setCurrentApp(ctx: StateContext<AppStateModel>, { appId }: SetCurrentApp) {
    const currentState = ctx.getState();
    // route params provide the id as a string, normalize it to avoid duplicate updates
    const normalizedAppId = Number(appId);

    if (currentState.currentAppId !== normalizedAppId) {
      ctx.patchState({
        currentAppId: normalizedAppId,
      });

      this.store.dispatch(new ResetPagination());
      this.store.dispatch(new ResetApiErrorMessage());
    }

    return this.resolveCurrentApp(ctx);
  }

  /**
   * Resolves the current app from the user apps list, or from the admin API when an
   * admin or superuser accesses an app they don't own.
   *
   * Args:
   *   ctx: The app state context.
   *
   * Returns:
   *   An observable completing once the admin app is loaded, or EMPTY if not needed.
   */
  private resolveCurrentApp(ctx: StateContext<AppStateModel>): Observable<unknown> {
    const { apps, currentAppId, currentApp } = ctx.getState();
    const appId = Number(currentAppId);
    const ownedApp = apps?.find(app => app.appId === appId);

    // always take the owned app from the list so it stays in sync with it
    if (ownedApp) {
      ctx.patchState({ currentApp: ownedApp });

      return EMPTY;
    }

    if (currentApp?.appId === appId) {
      return EMPTY;
    }

    ctx.patchState({ currentApp: null });

    // wait for the user apps list to know whether the app is owned
    const isAdminOrSuperuser = this.store.selectSnapshot(AuthState.isAdminOrSuperuser);
    if (!appId || !apps || !isAdminOrSuperuser) {
      return EMPTY;
    }

    return this.adminService.getApp(appId).pipe(
      tap(app => {
        if (ctx.getState().currentAppId === currentAppId) {
          ctx.patchState({ currentApp: app });
        }
      }),
      catchError(() => EMPTY),
    );
  }

  @Action(UpdateAvailable)
  updateAvailable(ctx: StateContext<AppStateModel>) {
    ctx.patchState({ appsManagerHasUpdateAvailable: true });
  }

  @Action(ResetAppState)
  resetAppState(ctx: StateContext<AppStateModel>) {
    ctx.patchState({
      apps: null,
      currentAppId: null,
      currentApp: null,
    });
  }

  @Action(ResetCurrentApp)
  resetCurrentApp(ctx: StateContext<AppStateModel>) {
    ctx.patchState({
      currentAppId: null,
      currentApp: null,
    });
    this.router.navigate(['/apps']);
  }

  @Action(UnlockUpdateRequest)
  unlockUpdateRequest(ctx: StateContext<AppStateModel>) {
    const currentState = ctx.getState();
    ctx.patchState({
      unlockUpdateIsLoading: true,
    });

    return this.appsUpdateService.unlockUpdate(currentState.currentAppId).pipe(
      tap(() => {
        ctx.dispatch(new UnlockUpdateSuccess());
      }),
      catchError(() => ctx.dispatch(new UnlockUpdateError())),
    );
  }

  @Action(UnlockUpdateSuccess)
  unlockUpdateSuccess(ctx: StateContext<AppStateModel>) {
    ctx.patchState({
      unlockUpdateIsLoading: false,
    });
    const currentAppId = ctx.getState().currentAppId;
    this.store.dispatch(new FetchPublishingDataRequest(currentAppId));
    this.router.navigate(['/publishing', currentAppId.toString()]);
  }

  @Action(UnlockUpdateError)
  unlockUpdateError(ctx: StateContext<AppStateModel>) {
    ctx.patchState({
      unlockUpdateIsLoading: false,
    });
  }
}
